import { type ComponentType } from 'react'

import {
  BulletedList,
  DiscoverBadge,
  DiscoverBadgeTypes,
  Header,
  type IIconComponentProps,
  Space,
  SubText,
  Text,
  WithTooltip,
} from '@procurify/ui'

import { RequestFeatureDemoButtonPresenter } from '@webapp/core/features/request-feature-demo/presenters/RequestFeatureDemoButtonPresenter'
import { TakeProductTourButtonPresenter } from '@webapp/core/features/take-product-tour/presenters/TakeProductTourButtonPresenter'
import { hasProductTour } from '@webapp/core/features/take-product-tour/utils'
import {
  type FeatureExpansionRequestType,
  type MarketoServiceError,
} from '@webapp/core/services/Marketo'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { FeaturePointsStyled } from './styles/FeaturePointsStyled'
import { FeatureShowcaseStyled } from './styles/FeatureShowcaseStyled'
import { HeroImageStyled } from './styles/HeroImageStyled'
import { HeroVideoStyled } from './styles/HeroVideoStyled'
import { useExpandableHeroVideo } from './useExpandableHeroVideo'

const ICON_SIZE = 14

export interface IProofPoint {
  icon: ComponentType<IIconComponentProps>
  text: string
}
export interface IFeaturePoint {
  featureTitle: string
  featureDescription: string
}
export interface IHeroVideoSource {
  src: string
  type: string
}
export interface IHeroVideo {
  poster: string
  /** Ordered by preference - the browser loads the first source it can play */
  sources: IHeroVideoSource[]
  /** Opt in to click-to-expand full-screen behaviour */
  isExpandable?: boolean
}

export interface IFeatureShowcaseProps {
  header: string
  description: string
  heroImage?: string
  heroVideo?: IHeroVideo
  heroImageAlt: string
  proofPoints?: IProofPoint[]
  featurePoints?: IFeaturePoint[]
  demoRequestContext: FeatureExpansionRequestType
  demoRequestDisclaimer: string
  demoRequestButtonText?: string
  tourTitle?: string
  onDemoRequested?: () => void
  onDemoRequestFailed?: (error: MarketoServiceError) => void
  onTourClicked?: () => void
  /**
   * Shown as "Add more..." appended to feature points -
   * `featurePoints` must also be provided
   */
  url?: string
  isButtonDisabled?: boolean
}

export const FeatureShowcase = ({
  header,
  description,
  heroImage,
  heroVideo,
  heroImageAlt,
  proofPoints = [],
  featurePoints = [],
  demoRequestContext,
  demoRequestDisclaimer,
  demoRequestButtonText,
  tourTitle,
  onDemoRequested,
  onDemoRequestFailed,
  onTourClicked,
  url,
  isButtonDisabled,
}: IFeatureShowcaseProps) => {
  const intl = useProcurifyIntl()

  const showTour = hasProductTour(demoRequestContext)

  // Called unconditionally to satisfy the rules of hooks; only wired to the
  // element below when the tab opts in via `heroVideo.isExpandable`.
  const { videoRef, isFullscreen, openFullscreen, onKeyDown } =
    useExpandableHeroVideo()

  return (
    <FeatureShowcaseStyled>
      <Space size='xlarge'>
        <DiscoverBadge
          type={DiscoverBadgeTypes.ADD_ON}
          text={intl.formatMessage(messages.addOnBadgeText)}
          fontSize={12}
        />

        <Space size='medium'>
          <Header as='h2'>{header}</Header>

          <Text>{description}</Text>

          {proofPoints.length > 0 && (
            <BulletedList>
              {proofPoints.map(({ icon: BulletIcon, text }, index) => (
                <BulletedList.Item
                  key={index}
                  iconComponent={<BulletIcon color='ai' size={ICON_SIZE} />}
                >
                  {text}
                </BulletedList.Item>
              ))}
            </BulletedList>
          )}
        </Space>

        <Space direction='horizontal' size='small' align='center'>
          <WithTooltip
            title={demoRequestDisclaimer}
            placement='top'
            isDisabled={isButtonDisabled}
          >
            <WithTooltip.Wrapper aria-label={demoRequestDisclaimer}>
              <RequestFeatureDemoButtonPresenter
                context={demoRequestContext}
                buttonText={demoRequestButtonText}
                variant={showTour ? 'secondary' : 'primary'}
                onSuccess={onDemoRequested}
                onError={onDemoRequestFailed}
                isButtonDisabled={isButtonDisabled}
              />
            </WithTooltip.Wrapper>
          </WithTooltip>

          <TakeProductTourButtonPresenter
            context={demoRequestContext}
            title={tourTitle}
            variant='primary'
            onTourOpened={onTourClicked}
          />
        </Space>
      </Space>

      {heroVideo ? (
        <HeroVideoStyled
          key={heroImageAlt}
          ref={heroVideo.isExpandable ? videoRef : undefined}
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          poster={heroVideo.poster}
          aria-label={heroImageAlt}
          controls={heroVideo.isExpandable ? isFullscreen : undefined}
          $isExpandable={heroVideo.isExpandable && !isFullscreen}
          role={heroVideo.isExpandable ? 'button' : undefined}
          tabIndex={heroVideo.isExpandable ? 0 : undefined}
          onClick={
            heroVideo.isExpandable && !isFullscreen ? openFullscreen : undefined
          }
          onKeyDown={
            heroVideo.isExpandable && !isFullscreen ? onKeyDown : undefined
          }
        >
          {heroVideo.sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </HeroVideoStyled>
      ) : (
        heroImage && <HeroImageStyled src={heroImage} alt={heroImageAlt} />
      )}

      {featurePoints.length > 0 && (
        <FeaturePointsStyled>
          {featurePoints.map(({ featureTitle, featureDescription }) => (
            <Space key={featureTitle}>
              <Text bold>{featureTitle}</Text>
              <SubText>{featureDescription}</SubText>
            </Space>
          ))}

          {url && (
            <a
              className='bill-pay-link'
              href={url}
              target='_blank'
              rel='noreferrer'
            >
              <Text bold noWrap>
                {intl.formatMessage(messages.andMoreLinkText)}
              </Text>
            </a>
          )}
        </FeaturePointsStyled>
      )}
    </FeatureShowcaseStyled>
  )
}

FeatureShowcase.displayName = 'FeatureShowcase'
